import {
    extractReportRows,
    extractReportSummary,
    findLargestReportArray,
    getContainerTeusFromRow,
    getReportNestedData,
    pickReportArray,
    pickTotalRecordCount,
} from "./fclReportHelpers";

/** @type {Record<string, { listKeys: string[], sections?: { id: string, title: string, listKeys: string[] }[] }>} */
export const FCL_REPORT_PARSE_CONFIG = {
    importBooking: {
        listKeys: ["bookingList", "BookingList", "bookings", "Bookings"],
        sections: [
            {
                id: "bookingComplexitySummary",
                title: "Booking Customer Wise Complexity Summary Report",
                listKeys: [
                    "bookingComplexitySummary_List",
                    "BookingComplexitySummary_List",
                    "bookingComplexitySummaryList",
                    "BookingComplexitySummaryList",
                ],
            },
        ],
    },
    importActiveShipment: {
        listKeys: [
            "activeShipmentList",
            "ActiveShipmentList",
            "shipmentList",
            "ShipmentList",
            "activeShipments",
            "ActiveShipments",
            "activeShipmentReportList",
            "ActiveShipmentReportList",
        ],
    },
    importDemurrage: {
        listKeys: [
            "containerDemurrageList",
            "ContainerDemurrageList",
            "demurrageList",
            "DemurrageList",
            "demurrage",
            "Demurrage",
        ],
    },
    importTrucking: {
        listKeys: ["truckingList", "TruckingList", "trucking", "Trucking"],
    },
    importStatusAudit: {
        listKeys: ["statusAuditList", "StatusAuditList", "auditList", "AuditList"],
    },
    importContainerTracking: {
        listKeys: [
            "containerTrackingList",
            "ContainerTrackingList",
            "containerList",
            "ContainerList",
            "trackingList",
            "TrackingList",
        ],
    },
    importArrivalNotice: {
        listKeys: [
            "arrivalNoticeList",
            "ArrivalNoticeList",
            "arrivalNoticeReportList",
            "ArrivalNoticeReportList",
            "arrivalNotices",
            "ArrivalNotices",
        ],
    },
    importEtaStatus: {
        listKeys: [
            "etaStatusList",
            "ETAStatusList",
            "etaStatusReportList",
            "ETAStatusReportList",
            "etaList",
            "EtaList",
        ],
    },
    importFileCancellationReport: {
        listKeys: [
            "fileCancellationList",
            "FileCancellationList",
            "fileCancellationReportList",
            "FileCancellationReportList",
            "cancellationList",
            "CancellationList",
            "fileCancellationReportData",
            "FileCancellationReportData",
        ],
    },
    importArrivalNoticePreAlertReport: {
        listKeys: [
            "arrivalNoticePreAlertList",
            "ArrivalNoticePreAlertList",
            "fclImpArrivalNoticePreAlertList",
            "FCLImpArrivalNoticePreAlertList",
            "preAlertList",
            "PreAlertList",
            "arrivalNoticeList",
            "ArrivalNoticeList",
        ],
    },
};

const pickSummaryObject = (nested, result) => {
    const fromExtract = extractReportSummary(result);
    if (fromExtract && Object.keys(fromExtract).length > 0) {
        return { ...fromExtract };
    }

    const candidates = [
        nested?.summary,
        nested?.Summary,
        nested?.primarySummary,
        nested?.PrimarySummary,
    ];
    for (const c of candidates) {
        if (c && typeof c === "object" && !Array.isArray(c)) return { ...c };
    }
    return {};
};

const enrichSummaryWithTeus = (summary, nested, primaryList = [], result = null) => {
    const totalTeusObj = nested?.totalTeus ?? nested?.TotalTeus;
    const fromObj =
        totalTeusObj?.totalTEUS ??
        totalTeusObj?.TotalTEUS ??
        totalTeusObj?.totalTeus ??
        totalTeusObj?.TotalTeus;

    const containerTeusList = nested?.containerTeusList ?? nested?.ContainerTeusList ?? [];
    const fromContainers = containerTeusList.reduce(
        (sum, row) => sum + (Number(getContainerTeusFromRow(row)) || 0),
        0
    );

    const primary = nested?.PrimarySummary ?? nested?.primarySummary ?? {};

    const totalRecords = pickTotalRecordCount({
        nested,
        result,
        summary,
        primaryList,
    });

    return {
        ...summary,
        totalBooking:
            summary.totalBooking ??
            summary.TotalBooking ??
            primary.TotalBooking ??
            primary.totalBooking,
        totalProfit:
            summary.totalProfit ??
            summary.TotalProfit ??
            primary.TotalProfit ??
            primary.totalProfit,
        totalTeus:
            summary.totalTeus ??
            summary.TotalTeus ??
            summary.totalTEUS ??
            summary.TotalTEUS ??
            fromObj ??
            (fromContainers > 0 ? fromContainers : undefined),
        totalRecords,
        totalRecord: totalRecords,
        totalContainers:
            summary.totalContainers ??
            summary.TotalContainers ??
            primary.TotalContainers ??
            primary.totalContainers,
    };
};

export const parseFclConfiguredReportResponse = (reportKey, result) => {
    const config = FCL_REPORT_PARSE_CONFIG[reportKey];
    if (!config) {
        return { primaryList: [], summary: {}, sections: {} };
    }

    const nested = getReportNestedData(result);
    let primaryList = pickReportArray(nested, config.listKeys);

    if (!primaryList.length) {
        primaryList = extractReportRows(result);
    }
    if (!primaryList.length) {
        primaryList = findLargestReportArray(result);
    }

    const summary = enrichSummaryWithTeus(
        pickSummaryObject(nested, result),
        nested,
        primaryList,
        result
    );

    const sections = {};
    (config.sections || []).forEach((section) => {
        sections[section.id] = pickReportArray(nested, section.listKeys);
    });

    return { primaryList, summary, sections };
};
